import * as React from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  ColourKey,
  EventIndicator,
  MobileCalendar,
  MobileCalendarEvent,
  MobileCalendarHeader,
  selectUser,
  useCacheQuery,
} from 'gossamer-front-end';
import { getFilter, getFirstDateOfMonth, getLastDateOfMonthPlusOne } from 'gossamer-unified';
import { formatEventUrl } from 'shared-front-end';
import { BookingExtendedView, EventExtendedView } from 'cb-model';
import { EVENT_TYPE_COLORS, EVENT_TYPE_LEGEND } from './CalendarCombined';
import type { CalendarCombinedProps } from './CalendarCombined';
import { makeWebsiteEventQueryFilters } from '../website-events/WebsiteEventFilters';

interface BookingOrEvent {
  eventId: number;
  title: string;
  startAt: string;
  endAt: string;
  bgColor: string;
}

export const CalendarMobile = (props: CalendarCombinedProps): React.JSX.Element => {
  const user = useSelector(selectUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date') ? new Date(searchParams.get('date') as string) : new Date();
  const setSelectedDate = (date: Date) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('date', date.toISOString());
    setSearchParams(newParams);
  };

  const eventsQuery = useCacheQuery(EventExtendedView, {
    where: {
      and: [
        getFilter('institution_id', 'eq', String(props.institutionId)),
        getFilter('type', 'ne', 'on-demand'),
        getFilter('end_at', 'gt', getFirstDateOfMonth(selectedDate).toISOString()),
        getFilter('start_at', 'lt', getLastDateOfMonthPlusOne(selectedDate).toISOString()),
        ...makeWebsiteEventQueryFilters(props.eventFilters, { includeFutureDefault: false }),
      ],
    },
    limit: 1000,
  });

  const bookingsQuery = useCacheQuery(
    BookingExtendedView,
    {
      where: {
        and: [
          getFilter('institution_id', 'eq', String(props.institutionId)),
          getFilter('user_id', 'eq', user?.userId || ''),
          getFilter('booking_status', 'eq', 'active'),
          getFilter('end_at', 'gt', getFirstDateOfMonth(selectedDate).toISOString()),
          getFilter('start_at', 'lt', getLastDateOfMonthPlusOne(selectedDate).toISOString()),
          ...makeWebsiteEventQueryFilters(props.eventFilters, {
            includeFutureDefault: false,
            includeVisibilityFilters: false,
            startFieldId: 'start_at',
          }),
        ],
      },
      limit: 1000,
    },
    !user,
  );

  const allData: BookingOrEvent[] = (eventsQuery.data || [])
    .map((event) => ({
      eventId: event.id,
      title: event.title,
      startAt: event.start_at,
      endAt: event.end_at,
      bgColor: EVENT_TYPE_COLORS[event.visibility_limit.toLowerCase()],
    }))
    .concat(
      (bookingsQuery.data || []).map((booking) => ({
        eventId: booking.event_id,
        title: booking.title,
        startAt: booking.start_at,
        endAt: booking.end_at,
        bgColor: EVENT_TYPE_COLORS['booking'],
      })),
    )
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const indicators: EventIndicator[] = allData.map((booking) => ({
    date: new Date(booking.startAt),
    colour: booking.bgColor,
  }));
  const eventsToday = allData.filter((booking) => {
    const eventDate = new Date(booking.startAt);
    return (
      eventDate.getFullYear() === selectedDate.getFullYear() &&
      eventDate.getMonth() === selectedDate.getMonth() &&
      eventDate.getDate() === selectedDate.getDate()
    );
  });

  const coloursUsed = eventsToday.map((booking) => booking.bgColor);
  return (
    <div>
      <MobileCalendarHeader selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
      <MobileCalendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} indicators={indicators} />
      {eventsToday.map((booking, index) => (
        <div key={index} className="mt-2">
          <MobileCalendarEvent
            title={booking.title}
            startAt={new Date(booking.startAt)}
            endAt={new Date(booking.endAt)}
            colorCode={booking.bgColor || '#ACBDE9'}
            navLink={formatEventUrl(booking.title, booking.eventId)}
          />
        </div>
      ))}
      {eventsToday.length === 0 && <div className="mt-2">No events on {selectedDate.toLocaleDateString()}</div>}
      <div className="mt-3" />
      {eventsToday.length > 0 && (
        <ColourKey items={EVENT_TYPE_LEGEND.filter((item) => coloursUsed.includes(item.bgColor))} />
      )}
    </div>
  );
};
